import { useState, useEffect, useCallback, useRef } from 'react';
import { Address, GeocodedAddress, LoadingState } from './AdherentMapTypes';

const BAN_API_URL = 'https://api-adresse.data.gouv.fr/search/';
const RATE_LIMIT = 9; // calls per second
const GEOCODE_CACHE_KEY = 'adherent_geocoding_cache';
// Cache will be valid for 30 days (in milliseconds)
const CACHE_VALIDITY = 30 * 24 * 60 * 60 * 1000;

interface GeocodingCache {
  [key: string]: {
    latitude: number;
    longitude: number;
    timestamp: number;
  };
}

// Helper function to create a unique address key
const getAddressKey = (address: Address): string => {
  // For SIRET-based addresses, use the SIRET as the key if available
  if (address.siret) {
    return `siret:${address.siret}`;
  }
  
  // Otherwise, normalize address components to create a unique key for each physical address
  const addrKey = [
    address.numeroVoirie || '',
    address.libelleVoie || '',
    address.codePostal || '',
    address.codeInsee || '',
    address.commune || '',
    address.codeDepartement || ''
  ].filter(Boolean).join('|').toLowerCase();
  
  return addrKey;
};

// Function to generate a unique string from the addresses array for memoization
const getAddressesSignature = (addresses: Address[]): string => {
  return addresses
    .map(addr => addr.id)
    .sort()
    .join('|');
};

export const useGeocoding = (addresses: Address[]) => {
  const [geocodedAddresses, setGeocodedAddresses] = useState<GeocodedAddress[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    total: 0,
    completed: 0,
    failed: 0,
    isComplete: false,
  });
  
  // Use a ref to track whether the component is mounted
  const isMountedRef = useRef(true);
  // Use a ref to track the current batch processing
  const processingRef = useRef(false);
  // Store the last processed signature in a ref to avoid unnecessary re-renders
  const lastProcessedSignatureRef = useRef<string>('');

  // Load cache from localStorage
  const loadCache = useCallback((): GeocodingCache => {
    try {
      const cached = localStorage.getItem(GEOCODE_CACHE_KEY);
      const parsedCache = cached ? JSON.parse(cached) : {};
      
      // Check for any expired entries and clean them up to avoid growing cache size indefinitely
      const now = Date.now();
      let hasExpired = false;
      
      Object.keys(parsedCache).forEach(key => {
        if (now - parsedCache[key].timestamp > CACHE_VALIDITY) {
          delete parsedCache[key];
          hasExpired = true;
        }
      });
      
      // Save the cleaned cache if we removed any expired entries
      if (hasExpired) {
        localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(parsedCache));
      }
      
      return parsedCache;
    } catch (error) {
      console.warn('Failed to load geocoding cache:', error);
      return {};
    }
  }, []);

  // Save cache to localStorage
  const saveCache = useCallback((cache: GeocodingCache) => {
    try {
      localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('Failed to save geocoding cache:', error);
    }
  }, []);

  // Get address from cache if available
  const getFromCache = useCallback((address: Address, cache: GeocodingCache): GeocodedAddress | null => {
    const addrKey = getAddressKey(address);
    
    if (cache[addrKey] && Date.now() - cache[addrKey].timestamp < CACHE_VALIDITY) {
      return {
        ...address,
        latitude: cache[addrKey].latitude,
        longitude: cache[addrKey].longitude,
        geocoded: true,
      };
    }
    
    return null;
  }, []);

  // Geocode a single address using API
  const geocodeAddressApi = useCallback(async (address: Address, cache: GeocodingCache): Promise<GeocodedAddress | null> => {
    // Create a more accurate search query for the BAN API
    const addressComponents = [];
    if (address.numeroVoirie) addressComponents.push(address.numeroVoirie);
    if (address.libelleVoie) addressComponents.push(address.libelleVoie);
    
    // For the municipality, prefer commune over adresse field
    const municipalityPart = [];
    if (address.codePostal) municipalityPart.push(address.codePostal);
    if (address.commune) municipalityPart.push(address.commune);
    
    const formattedAddress = addressComponents.join(' ');
    const formattedMunicipality = municipalityPart.join(' ');
    
    // Create the query string for the API
    const searchQuery = [formattedAddress, formattedMunicipality].filter(Boolean).join(', ');
    const addrKey = getAddressKey(address);

    try {
      // Only proceed if component is still mounted
      if (!isMountedRef.current) {
        return null;
      }

      const response = await fetch(
        `${BAN_API_URL}?q=${encodeURIComponent(searchQuery)}&limit=1&autocomplete=0`
      );
      const data = await response.json();

      // Check again if component is still mounted before updating state
      if (!isMountedRef.current) {
        return null;
      }

      if (data.features && data.features.length > 0) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;
        
        // Update cache
        cache[addrKey] = {
          latitude,
          longitude,
          timestamp: Date.now(),
        };
        saveCache(cache);

        return {
          ...address,
          latitude,
          longitude,
          geocoded: true,
        };
      }
    } catch (error) {
      console.error(`Geocoding failed for address: ${searchQuery}`, error);
    }
    return null;
  }, [saveCache]);

  // Process addresses with rate limiting
  useEffect(() => {
    // Reset mounted ref when effect runs
    isMountedRef.current = true;
    
    // Get current addresses signature
    const addressesSignature = getAddressesSignature(addresses);
    
    // Always start a new geocoding process when the component mounts or when addresses change
    const processAddresses = async () => {
      // If we're already processing a batch, don't start a new one
      if (processingRef.current) {
        return;
      }

      // Set processing flag to true
      processingRef.current = true;
      
      // Reset state values to ensure a clean start
      setGeocodedAddresses([]);
      
      // Load the cache first
      const cache = loadCache();
      
      // Step 1: Get cached addresses immediately
      const cachedResults: GeocodedAddress[] = [];
      const uncachedAddresses: Address[] = [];
      
      // Separate cached from uncached addresses
      addresses.forEach(address => {
        const fromCache = getFromCache(address, cache);
        if (fromCache) {
          cachedResults.push(fromCache);
        } else {
          uncachedAddresses.push(address);
        }
      });
      
      // Update loading state with the total count
      setLoadingState({
        total: addresses.length,
        completed: cachedResults.length,
        failed: 0,
        isComplete: uncachedAddresses.length === 0 // Complete if all are cached
      });
      
      // If everything is from cache, we're done
      if (uncachedAddresses.length === 0) {
        console.log('All addresses loaded from cache');
        setGeocodedAddresses(cachedResults);
        lastProcessedSignatureRef.current = addressesSignature;
        processingRef.current = false;
        return;
      }
      
      // Process uncached addresses
      console.log(`Processing ${uncachedAddresses.length} uncached addresses, ${cachedResults.length} from cache`);
      
      // Step 2: Process remaining addresses in batches for rate limiting
      const batchSize = RATE_LIMIT;
      const batches = Math.ceil(uncachedAddresses.length / batchSize);
      
      let apiResults: GeocodedAddress[] = [];
      
      for (let i = 0; i < batches; i++) {
        // Check if component is still mounted
        if (!isMountedRef.current) {
          processingRef.current = false;
          return;
        }
        
        const start = i * batchSize;
        const end = Math.min(start + batchSize, uncachedAddresses.length);
        const batch = uncachedAddresses.slice(start, end);
        
        const batchResults = await Promise.all(
          batch.map(addr => geocodeAddressApi(addr, cache))
        );
        
        // Check again if component is still mounted
        if (!isMountedRef.current) {
          processingRef.current = false;
          return;
        }
        
        const successfulResults = batchResults.filter((result): result is GeocodedAddress => result !== null);
        apiResults = [...apiResults, ...successfulResults];
        
        // Update the loading state
        setLoadingState(prev => {
          const newCompleted = prev.completed + batch.length;
          const newFailed = prev.failed + (batch.length - successfulResults.length);
          return {
            ...prev,
            completed: newCompleted,
            failed: newFailed,
            isComplete: newCompleted + newFailed >= prev.total
          };
        });
        
        // Update geocoded addresses so far with both cached and current API results
        const currentResults = [...cachedResults, ...apiResults];
        setGeocodedAddresses(currentResults);
        
        // Wait for 1 second before processing next batch to respect rate limit
        if (i < batches - 1) { // Don't wait after the last batch
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Set final state
      lastProcessedSignatureRef.current = addressesSignature;
      processingRef.current = false;
    };

    // Always start the geocoding process when this effect runs
    processAddresses();
    
    // Clean up when component unmounts
    return () => {
      isMountedRef.current = false;
    };
  }, [addresses, geocodeAddressApi, getFromCache, loadCache]);

  return {
    geocodedAddresses,
    loadingState,
  };
}; 